<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Announcements"
      subtitle="Manage school announcements"
      icon="fas fa-bullhorn"
      gradient="blue"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search title or content"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Status"
          label-for="filterActive"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableData.selected.active"
            input-id="filterActive"
            label="name"
            :options="activeOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Action"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BButton
            size="sm"
            type="button"
            variant="primary"

            @click.prevent="createItem"
          >
            Add Announcement
          </BButton>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableData.busy"
            @click.prevent="editItem(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableData.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(content)="{ item }">
        <span :title="item.content">{{ trimText(item.content, 80) }}</span>
      </template>

      <template #cell(image_url)="{ item }">
        <img
          v-if="item.image_url"
          :src="resolveStoragePath(item.image_url)"
          alt="announcement"
          style="width: 48px; height: 48px; object-fit: cover; border-radius: 8px;"
        >
        <span
          v-else
          class="text-muted small"
        >No image</span>
      </template>

      <template #cell(is_active)="{ item }">
        <BBadge :variant="item.is_active ? 'success' : 'secondary'">
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalForm"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Announcement' : 'Add Announcement'"
    >
      <ValidationObserver ref="formData">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="title"
                name="title"
                rules="required|max:255"
              >
                <BFormGroup
                  label="Title"
                  label-for="fTitle"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fTitle"
                    v-model="formData.title"
                    placeholder="enter title"
                    maxlength="255"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="content"
                name="content"
                rules="required|min:10|max:1000"
              >
                <BFormGroup
                  label="Content"
                  label-for="fContent"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="fContent"
                    v-model="formData.content"
                    placeholder="enter announcement content"
                    rows="4"
                    maxlength="1000"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <BFormGroup
                label="Image (Optional)"
                label-for="fImage"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormFile
                  id="fImage"
                  accept="image/*"
                  :disabled="state.busy"
                  @change="handleImageUpload"
                />
                <small class="text-muted">Allowed: JPG, PNG, GIF, WEBP (max 5MB)</small>

                <div
                  v-if="announcementImagePreview"
                  class="mt-2"
                >
                  <img
                    :src="announcementImagePreview"
                    alt="preview"
                    style="width: 120px; height: 80px; object-fit: cover; border-radius: 8px;"
                  >
                </div>

                <BFormCheckbox
                  v-if="state.editing && formData.image_url"
                  id="remove_announcement_image"
                  v-model="formData.remove_image"
                  class="mt-2"
                  :disabled="state.busy"
                  :value="1"
                  :unchecked-value="0"
                >
                  Remove current image
                </BFormCheckbox>
              </BFormGroup>
            </BCol>

            <BCol cols="12">
              <BFormGroup class="mb-2">
                <BFormCheckbox
                  id="is_active"
                  v-model="formData.is_active"
                  switch
                  value="1"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateForm"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminAnnouncement } from '@/services'

export default {
  name: 'VAdminAnnouncements',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter, MxImageUrl],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Announcements' }),

  data () {
    return {
      state: { busy: false, editing: false },
      announcementImagePreview: '',
      formData: {
        id: '',
        title: '',
        content: '',
        image: null,
        image_url: '',
        remove_image: 0,
        is_active: 1
      },
      activeOptions: [
        { id: 1, name: 'Active' },
        { id: 0, name: 'Inactive' }
      ],
      tableData: {
        busy: false,
        selected: {
          active: null
        },
        filter: {
          search: '',
          is_active: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'updated_at',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'image_url', label: 'Image', class: 'text-center' },
          { mobile: 1, key: 'title', class: 'min-w-200px' },
          { mobile: 2, key: 'content', class: 'min-w-300px' },
          { mobile: 3, key: 'is_active', class: 'text-center' },
          { mobile: 4, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'tableData.selected.active' (active) {
      this.tableData.filter.is_active = active !== null && active !== undefined ? active.id : ''
    }
  },

  methods: {
    trimText (text, length = 80) {
      if (!text) return ''
      return text.length > length ? `${text.slice(0, length)}...` : text
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        title: filter.search,
        content: filter.search,
        is_active: filter.is_active
      }

      return SAdminAnnouncement.getList(payload)
        .then(({ data: response }) => {
          const data = response?.data || response
          this.tableData.totalRows = data?.total_rows || 0
          return data?.items || []
        })
        .catch(() => {
          this.tableData.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableData.busy = false
        })
    },

    createItem () {
      this.state.editing = false
      this.announcementImagePreview = ''
      this.formData = {
        id: '',
        title: '',
        content: '',
        image: null,
        image_url: '',
        remove_image: 0,
        is_active: 1
      }
      this.$refs.modalForm.show()
    },

    editItem (item) {
      this.state.editing = true
      this.announcementImagePreview = item.image_url ? this.resolveStoragePath(item.image_url) : ''
      this.formData = {
        id: item.id,
        title: item.title,
        content: item.content,
        image: null,
        image_url: item.image_url || '',
        remove_image: 0,
        is_active: item.is_active ? 1 : 0
      }
      this.$refs.modalForm.show()
    },

    handleImageUpload (event) {
      const file = event?.target?.files?.[0] || null
      this.formData.image = file
      this.formData.remove_image = 0

      if (!file) {
        this.announcementImagePreview = this.formData.image_url ? this.resolveStoragePath(this.formData.image_url) : ''
        return
      }

      this.announcementImagePreview = URL.createObjectURL(file)
    },

    async validateForm () {
      await this.$refs.formData.validate().then(async validForm => {
        if (!validForm) return this.swalInvalid()

        this.swalConfirm({
          html: this.state.editing ? '<h6>Update this announcement?</h6>' : '<h6>Create this announcement?</h6>',
          confirmButtonText: this.state.editing ? 'Update' : 'Create',
          preConfirm: async () => {
            this.state.busy = true

            const action = this.state.editing
              ? SAdminAnnouncement.update(this.formData.id, this.formData)
              : SAdminAnnouncement.store(this.formData)

            return action.then(() => {
              this.$refs.modalForm.hide()
              this.$refs.tableData.refetch()
              this.swalSuccess(`Announcement ${this.state.editing ? 'updated' : 'created'} successfully`)
            }).catch(({ message }) => {
              if (message?.errors) {
                this.$refs.formData.setErrors(message.errors)
              }
            }).finally(() => {
              this.state.busy = false
            })
          }
        })
      })
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({
        title: 'Delete announcement',
        text: `Are you sure you want to delete "${item.title}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.state.busy = true
        SAdminAnnouncement.destroy(item.id).then(() => {
          this.swalSuccess('Announcement deleted successfully')
          this.$refs.tableData.refetch()
        }).catch(error => {
          this.swalError(error.response?.data?.message || 'Failed to delete announcement')
        }).finally(() => {
          this.state.busy = false
        })
      }
    }
  }
}
</script>
